/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react'

import { ErrorProps } from './common'

interface UseFetchQueryResult<T> {
  data: T | undefined
  apiLoading: boolean
  apiError: ErrorProps | undefined
}

/**
 * Generic data-fetching hook. Handles the common didCancel/loading/error pattern
 * used by 8+ hooks in this codebase.
 *
 * @param fetchFn  Async function that returns the desired data
 * @param deps     Dependency array (typically the query parameters)
 * @param errorTitle  User-facing error title on failure
 * @param options  { guard?: boolean, initialValue?: T, onBeforeFetch?: () => void }
 */
export function useFetchQuery<T>(
  fetchFn: () => Promise<T>,
  deps: any[],
  errorTitle: string,
  options: {
    guard?: boolean
    initialValue?: T
    onBeforeFetch?: () => void
  } = {},
): UseFetchQueryResult<T> {
  const { guard = true, initialValue, onBeforeFetch } = options
  const [data, setData] = useState<T | undefined>(initialValue)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorProps>()
  const onBeforeFetchRef = useRef(onBeforeFetch)
  onBeforeFetchRef.current = onBeforeFetch

  const memoizedFetch = useCallback(() => fetchFn(), deps)

  useEffect(() => {
    let didCancel = false

    const fetchData = async () => {
      if (didCancel) return
      onBeforeFetchRef.current?.()
      if (!didCancel) setLoading(true)

      try {
        const result = await memoizedFetch()
        if (!didCancel) {
          setData(result)
          setError(undefined)
        }
      } catch (e) {
        console.log(e)
        if (!didCancel) setError({ title: errorTitle, message: '' })
      }

      if (!didCancel) {
        setLoading(false)
      }
    }

    if (guard) fetchData()

    return () => {
      didCancel = true
    }
  }, [guard, memoizedFetch])

  return {
    data,
    apiLoading: loading,
    apiError: error,
  }
}
